import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronLeft, ChevronRight, Loader2, ScrollText } from 'lucide-react';
import { fetchJournalActions } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const horodatage = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** La couleur dit la nature de l'acte, pas sa gravité. */
const teinte: Record<string, string> = {
  creation: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  modification: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  suppression: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  connexion: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  connexion_refusee: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  deconnexion: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  droits_modifies: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  export: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  coffre_ouvert: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  document_telecharge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

/**
 * Le journal des actions sensibles.
 *
 * Il répond à une question qui n'avait pas de réponse : qu'a-t-on fait des
 * données de cette personne ? Une fiche d'adhérent supprimée par erreur ne
 * laissait aucune trace de qui l'avait supprimée, ni même qu'elle avait
 * existé.
 *
 * Ce qu'on y lit est délibérément pauvre : qu'une fiche a été modifiée, par
 * qui, et quels champs ont bougé — jamais leur contenu. Recopier les valeurs
 * ferait de ce journal une seconde base de données personnelles, celle-là
 * même qu'il est censé protéger.
 *
 * Réservé aux administrateurs. Ouvert à tout le bureau, il deviendrait un
 * outil de surveillance entre bénévoles, ce qu'il n'est pas.
 */
export default function JournalActionsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [action, setAction] = useState('');
  const [auteur, setAuteur] = useState('');
  const [page, setPage] = useState(1);

  const estAdmin = user?.roles?.includes('ROLE_ADMIN') === true;

  const { data, isPending } = useQuery({
    queryKey: ['journal-actions', action, auteur, page],
    queryFn: () => fetchJournalActions({ action, auteur, page }),
    enabled: estAdmin,
  });

  if (authLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!estAdmin) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Le journal est réservé aux administrateurs du site. Il sert à répondre à qui demande
              ce qui a été fait de ses données, pas à surveiller le travail des uns et des autres.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const lignes = data?.lignes ?? [];
  const total = data?.total ?? 0;
  const parPage = data?.parPage ?? 50;
  const pages = Math.max(1, Math.ceil(total / parPage));

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <ScrollText className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Journal des actions
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Qui a fait quoi, et quand, sur les données des personnes.
          </p>
        </div>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2">
        Le journal note qu'une chose a eu lieu, jamais son contenu : « téléphone et adresse
        modifiés », pas l'ancien numéro. Il se conserve trois ans, puis s'efface tout seul — une
        traçabilité sans fin ne serait plus de la traçabilité.
      </p>

      <div className="flex flex-wrap gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400">
          Nature
          <select
            className="input mt-1"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Toutes</option>
            {Object.entries(data?.actions ?? {}).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Auteur
          <select
            className="input mt-1"
            value={auteur}
            onChange={(e) => {
              setAuteur(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Tous</option>
            {(data?.auteurs ?? []).map((a) => (
              <option key={a.id} value={String(a.id)}>{a.nom || `#${a.id}`}</option>
            ))}
          </select>
        </label>
      </div>

      {isPending ? (
        <div className="p-8 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : lignes.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Rien au journal pour ce filtre.
          </p>
        </div>
      ) : (
        <>
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-2 font-medium whitespace-nowrap">Quand</th>
                  <th className="px-4 py-2 font-medium">Nature</th>
                  <th className="px-4 py-2 font-medium">Auteur</th>
                  <th className="px-4 py-2 font-medium">Ce qui s'est passé</th>
                  <th className="px-4 py-2 font-medium whitespace-nowrap">Depuis</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {lignes.map((l) => (
                  <tr key={l.id}>
                    <td className="px-4 py-2 whitespace-nowrap tabular-nums text-gray-600 dark:text-gray-400">
                      {l.quandLe ? horodatage.format(new Date(l.quandLe)) : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                          teinte[l.action] ?? 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {l.actionLabel}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                      {l.auteurNom ?? <span className="text-gray-400">personne connectée</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                      {l.resume}
                      {l.cibleId !== null && l.cibleType && (
                        <span className="text-xs text-gray-400"> (#{l.cibleId})</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-400 tabular-nums whitespace-nowrap">
                      {l.adresseIp ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
              {total} trace{total > 1 ? 's' : ''} — page {page} sur {pages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="btn-outline inline-flex items-center gap-1 text-sm py-1.5 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" /> Précédent
              </button>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
                className="btn-outline inline-flex items-center gap-1 text-sm py-1.5 disabled:opacity-40"
              >
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}

      <button
        type="button"
        onClick={() => navigate('/admin/parametres')}
        className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
      >
        Retour aux paramètres
      </button>
    </div>
  );
}
